/* React */
import React, { useEffect } from "react";

/* Ionic */
import { IonRow, IonCol } from "@ionic/react";

/* React-Spring */
import { useTransition, animated } from "@react-spring/web";

/* React-Redux */
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import { setToastMessage } from "src/redux/features/toast-message/toastMessageSlice";

/* Components */
import FilterToolbar from "src/components/insights/filter-toolbar/FilterToolbar";

/* Stylesheet */
import styles from "./ChartLayoutWrapper.module.scss";

/* Interfaces */
import { LayoutProps } from "src/shared/interfaces/common/Layout";
import { ClientErrorProps } from "src/shared/interfaces/common/GlobalMessage";
import { getRowStyles } from "./helper";

interface ChartWrapperProps {
  layout: LayoutProps;
  error?: ClientErrorProps;
}

const ChartLayoutWrapper: React.FC<ChartWrapperProps> = ({
  layout,
  error,
}) => {
  const dispatch = useDispatch();
  const loadingState = useSelector(
    (state: RootState) => state.loadingScreen
  );
  const { showContent } = loadingState;

  const transitions = useTransition(showContent, {
    from: { transform: "translateY(542px)", opacity: 0 },
    enter: { transform: "translateY(0)", opacity: 1 },
    config: { tension: 150, friction: 20 },
  });

  useEffect(() => {
    if (error) {
      dispatch(
        setToastMessage({
          type: error.type,
          message: error.message,
          show: true,
        })
      );
    }
  }, [dispatch, error]);

  return (
    <div className={styles.chart_layout_wrapper}>
      {transitions(
        (style, item) =>
          item && (
            <>
              <FilterToolbar />
              <animated.div
                style={style}
                className={styles.grid_container}
              >
                {layout.rows?.map((row, rowIndex) => (
                  <IonRow
                    key={rowIndex}
                    style={{
                      flexDirection:
                        rowIndex % 2 !== 0
                          ? "row-reverse"
                          : "row",
                    }}
                    className={styles.row}
                  >
                    {row.cols.map((col, colIndex) => (
                      <IonCol
                        key={colIndex}
                        style={{
                          padding: getRowStyles(
                            rowIndex,
                            layout.rows.length
                          ).padding,
                        }}
                        className={styles.col}
                        size={
                          col.fullWidth ? "12" : col.size
                        }
                      >
                        {col.component}
                      </IonCol>
                    ))}
                  </IonRow>
                ))}
              </animated.div>
            </>
          )
      )}
    </div>
  );
};

export default ChartLayoutWrapper;
